import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Bell, Trophy, Flame, Zap, X, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";

function typeStyle(t) {
  if (t === "contest") return { icon: Trophy, color: "#FFD600" };
  if (t === "trade") return { icon: Zap, color: "#00F0FF" };
  if (t === "heat") return { icon: Flame, color: "#FF0055" };
  return { icon: Bell, color: "#B026FF" };
}

function timeAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    return `${d}d`;
  } catch { return ""; }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const fetchAll = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data || []);
      setUnread((r.data || []).filter(n => !n.read).length);
    } catch {}
  }, []);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 20000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((xs) => xs.map(x => ({ ...x, read: true })));
      setUnread(0);
    } catch {}
  };

  return (
    <>
      <button
        className="relative w-9 h-9 grid place-items-center rounded-full glass hover:bg-white/10"
        onClick={() => setOpen(true)}
        data-testid="notification-bell"
        aria-label="notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold grid place-items-center"
            style={{ background: "#FF0055", color: "#fff" }}
            data-testid="notification-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              className="fixed inset-0 z-[60]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="notifications-drawer"
            >
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "tween", duration: 0.28 }}
                className="absolute right-0 top-0 h-full w-full max-w-md glass-strong border-l border-white/10 p-4 overflow-y-auto"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl font-bold">Notifications</h3>
                  <button
                    onClick={() => setOpen(false)}
                    className="w-9 h-9 grid place-items-center rounded-full hover:bg-white/10"
                    data-testid="close-notifications"
                    aria-label="close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={markAllRead}
                    className="chip hover:bg-white/10"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }}
                    data-testid="mark-all-read"
                  >
                    <Check className="w-3 h-3" /> Mark all read
                  </button>
                  <span className="text-xs text-white/50">{unread} unread</span>
                </div>

                <div className="mt-3 space-y-2">
                  {items.length === 0 && (
                    <div className="glass rounded-2xl p-5 text-sm text-white/60 text-center">
                      You're all caught up ✨
                    </div>
                  )}
                  {items.map((n) => {
                    const { icon: Icon, color } = typeStyle(n.type);
                    const Card = (
                      <div
                        data-testid={`notification-${n.notification_id}`}
                        className={`glass rounded-2xl p-3 flex gap-3 border ${n.read ? "border-white/5" : "border-white/20"} ${n.link ? "card-hover cursor-pointer" : ""}`}
                      >
                        <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: `${color}22`, border: `1px solid ${color}55` }}>
                          <Icon className="w-4 h-4" style={{ color }} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{n.title}</p>
                          <p className="text-xs text-white/65 mt-0.5">{n.body}</p>
                          <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{timeAgo(n.created_at)} ago</p>
                        </div>
                        {!n.read && <span className="w-2 h-2 rounded-full mt-1.5" style={{ background: "#00F0FF" }} aria-label="unread" />}
                      </div>
                    );
                    return n.link ? (
                      <Link key={n.notification_id} to={n.link} onClick={() => setOpen(false)}>{Card}</Link>
                    ) : (
                      <div key={n.notification_id}>{Card}</div>
                    );
                  })}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
